import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ArrowsLeftRight, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable replenishment dialog.
 *
 * Props:
 *   row             – { variant_id, sku_code, on_hand_available, on_hand_reserved, ... }
 *   targetLocation  – { id, location_code }  (destination)
 *   onClose()       – user clicked cancel / closed the dialog
 *   onDone()        – a transfer succeeded (refresh parent view)
 */
export default function ReplenishDialog({ row, targetLocation, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [suggestion, setSuggestion] = useState(null);
  const [qty, setQty] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const { data } = await axios.get(
          `${API}/api/lookups/replenish-suggestion`,
          { params: { variant_id: row.variant_id, exclude_location_id: targetLocation.id }, withCredentials: true },
        );
        if (!live) return;
        setSuggestion(data);
        if (data?.source) {
          // Default: move enough to cover the reservation gap (or 10, whichever is smaller), capped by source free
          const gap = Math.max(0, (row.on_hand_reserved ?? 0) - (row.on_hand_available ?? 0));
          const want = Math.max(gap, Math.min(10, data.source.free));
          setQty(Math.max(1, Math.min(want, data.source.free)));
        }
      } catch (e) {
        if (live) setErr(e?.response?.data?.detail || 'Failed to load suggestion');
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, [row.variant_id, targetLocation.id]);

  const confirm = async () => {
    if (!suggestion?.source || qty <= 0) return;
    setSubmitting(true); setErr(null);
    try {
      await axios.post(
        `${API}/api/inventory/transfer`,
        {
          from_location_id: suggestion.source.location_id,
          to_location_id: targetLocation.id,
          variant_id: row.variant_id,
          quantity: qty,
        },
        { withCredentials: true },
      );
      toast.success(`Moved ${qty} × ${row.sku_code} from ${suggestion.source.location_code} to ${targetLocation.location_code}`, {
        icon: <CheckCircle weight="fill" />,
        // Deterministic assertion hook for the testing agent
        className: 'data-testid-replenish-success-toast',
        id: 'replenish-success-toast',
        unstyled: false,
        // Sonner forwards arbitrary html props on the inner element via `descriptionProps` / `attributes`; we render
        // a data-testid via a dedicated description node so the toast is uniquely queryable.
        description: <span data-testid="replenish-success-toast">{`${qty} × ${row.sku_code} → ${targetLocation.location_code}`}</span>,
      });
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const src = suggestion?.source;
  const max = src?.free || 0;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent data-testid="replenish-dialog" className="border border-border rounded-none max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowsLeftRight size={20} weight="bold" className="text-amber-500" />
            Replenish {row.sku_code}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Move stock into <span className="font-mono font-bold">{targetLocation.location_code}</span> from the best-stocked alternative location.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="text-sm text-muted-foreground py-4">Finding best source…</div>}

        {!loading && !src && (
          <div data-testid="replenish-no-source" className="p-3 border border-amber-200 bg-amber-50 text-amber-800 text-xs">
            {suggestion?.reason || 'No source location has free stock of this variant.'}
          </div>
        )}

        {!loading && src && (
          <div className="space-y-3" data-testid="replenish-suggestion-panel">
            <div className="border border-border p-3 bg-muted">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Suggested source</div>
              <div className="font-mono text-lg font-bold" data-testid="replenish-source-code">{src.location_code}</div>
              <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
                Zone {src.zone || '—'} · On-hand {src.on_hand} · Reserved there {src.reserved_at_location} · Free <span className="text-emerald-700 font-bold">{src.free}</span>
                {src.same_warehouse ? ' · Same warehouse' : ' · Cross-warehouse'}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Quantity to move</label>
              <Input
                data-testid="replenish-qty-input"
                type="number"
                min={1}
                max={max}
                value={qty}
                onChange={(e) => setQty(Math.max(1, Math.min(max, Number(e.target.value) || 0)))}
                className="font-mono text-lg"
              />
              <div className="text-[10px] text-muted-foreground font-mono mt-1">Max {max} (free stock at source)</div>
            </div>
          </div>
        )}

        {err && (
          <div data-testid="replenish-error" className="p-2 border border-rose-200 bg-rose-50 text-rose-700 text-xs flex items-center gap-2">
            <Warning weight="bold" size={14} />{err}
          </div>
        )}

        <DialogFooter>
          <Button data-testid="replenish-cancel" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button
            data-testid="replenish-confirm"
            onClick={confirm}
            disabled={loading || submitting || !src || qty <= 0 || qty > max}
            className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold"
          >
            {submitting ? 'Moving…' : `Move ${qty || 0} units`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
